<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/analyses">Analyses</a></li>
        <li class="breadcrumb-item active">New Analysis</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">Request Analysis</h1>
    <p class="text-muted mb-0 mt-1">Select a processed map and configure analysis options</p>
  </div>
  <button class="btn btn-outline-secondary" (click)="goBack()">
    <i class="bi bi-arrow-left me-2"></i>Back
  </button>
</div>

<!-- Pricing Explainer (upfront, visible across all steps) -->
<div class="alert alert-info d-flex align-items-start mb-4" role="status">
  <i class="bi bi-info-circle-fill me-2 mt-1"></i>
  <div class="flex-grow-1">
    <strong>{{ perAcreLabel }}</strong>
    <span class="ms-2">— per-acre pricing. Pick a field and we'll show your exact total before checkout.</span>
  </div>
</div>

<!-- Stepper Progress -->
<div class="card mb-4">
  <div class="card-body py-3">
    <div class="d-flex justify-content-between align-items-center position-relative">
      <!-- Step 1 -->
      <div class="stepper-step" [class.active]="isStepActive(1)" [class.completed]="isStepComplete(1) && !isStepActive(1)">
        <button class="stepper-circle" (click)="goToStep(1)" [attr.aria-label]="'Step 1: Select Field'">
          <span *ngIf="isStepComplete(1) && !isStepActive(1)"><i class="bi bi-check-lg"></i></span>
          <span *ngIf="!isStepComplete(1) || isStepActive(1)">1</span>
        </button>
        <span class="stepper-label d-none d-md-block">
          <span *ngIf="isStepComplete(1) && selectedField; else step1Default">{{ selectedField!.name }}</span>
          <ng-template #step1Default>Select Field</ng-template>
        </span>
      </div>
      <div class="stepper-line" [class.completed]="isStepComplete(1)"></div>

      <!-- Step 2 -->
      <div class="stepper-step" [class.active]="isStepActive(2)" [class.completed]="isStepComplete(2) && !isStepActive(2)">
        <button class="stepper-circle" (click)="goToStep(2)" [attr.aria-label]="'Step 2: Select Map'" [disabled]="!isStepComplete(1)">
          <span *ngIf="isStepComplete(2) && !isStepActive(2)"><i class="bi bi-check-lg"></i></span>
          <span *ngIf="!isStepComplete(2) || isStepActive(2)">2</span>
        </button>
        <span class="stepper-label d-none d-md-block">
          <span *ngIf="isStepComplete(2) && selectedMap; else step2Default">{{ selectedMap!.mapName }}</span>
          <ng-template #step2Default>Select Map</ng-template>
        </span>
      </div>
      <div class="stepper-line" [class.completed]="isStepComplete(2)"></div>

      <!-- Step 3 -->
      <div class="stepper-step" [class.active]="isStepActive(3)" [class.completed]="isStepComplete(3) && !isStepActive(3)">
        <button class="stepper-circle" (click)="goToStep(3)" [attr.aria-label]="'Step 3: Analysis Options'" [disabled]="!isStepComplete(2)">
          <span *ngIf="isStepComplete(3) && !isStepActive(3)"><i class="bi bi-check-lg"></i></span>
          <span *ngIf="!isStepComplete(3) || isStepActive(3)">3</span>
        </button>
        <span class="stepper-label d-none d-md-block">
          <span *ngIf="isStepComplete(3) && selectedAnalysisType; else step3Default">{{ selectedAnalysisType!.name }}</span>
          <ng-template #step3Default>Analysis Options</ng-template>
        </span>
      </div>
      <div class="stepper-line" [class.completed]="isStepComplete(3)"></div>

      <!-- Step 4 -->
      <div class="stepper-step" [class.active]="isStepActive(4)" [class.completed]="isStepComplete(4) && !isStepActive(4)">
        <button class="stepper-circle" (click)="goToStep(4)" [attr.aria-label]="'Step 4: Review'" [disabled]="!isStepComplete(3)">
          <span *ngIf="isStepComplete(4) && !isStepActive(4)"><i class="bi bi-check-lg"></i></span>
          <span *ngIf="!isStepComplete(4) || isStepActive(4)">4</span>
        </button>
        <span class="stepper-label d-none d-md-block">Review</span>
      </div>
      <div class="stepper-line" [class.completed]="isStepComplete(4)"></div>

      <!-- Step 5: Payment (paywall lands here — billing-payments § 9.4) -->
      <div class="stepper-step" [class.active]="isStepActive(5)">
        <!-- goToStep() only allows backward navigation, so this button
             should only be enabled once the user has actually reached
             (or passed) step 5 — gating on isStepComplete(4) matches the
             stepper-line behavior and avoids the appearance-of-broken
             "enabled but does nothing" state. -->
        <button class="stepper-circle" (click)="goToStep(5)" [attr.aria-label]="'Step 5: Pay & Run Analysis'" [disabled]="!isStepComplete(4)">
          <span>5</span>
        </button>
        <span class="stepper-label d-none d-md-block">Pay &amp; Run</span>
      </div>
    </div>
  </div>
</div>

<!-- Step Content + Sidebar Help Panel -->
<div class="row">
  <!-- Main Step Content -->
  <div class="col-lg-8">
    <div class="card">
      <div class="card-body">

        <!-- ============ STEP 1: Select Field ============ -->
        <div *ngIf="currentStep() === 1">
          <h5 class="mb-3"><i class="bi bi-geo-alt me-2"></i>Select Field</h5>
          <p class="text-muted mb-4">Choose the field you want to analyze.</p>

          <form [formGroup]="fieldForm">
            <div class="mb-3">
              <label for="fieldSelect" class="form-label">Field <span class="text-danger">*</span></label>
              <select id="fieldSelect" class="form-select" formControlName="fieldId">
                <option value="">-- Select a field --</option>
                <option *ngFor="let field of fields()" [value]="field.id">
                  {{ field.name }} ({{ field.acreage }} acres - {{ field.cropType }}){{ field.customerName ? ' — ' + field.customerName : '' }}
                </option>
              </select>
              <div *ngIf="fieldForm.get('fieldId')?.touched && fieldForm.get('fieldId')?.invalid" class="invalid-feedback d-block">
                Please select a field.
              </div>
              <div class="mt-2">
                <a routerLink="/logbook/fields/add" class="small text-decoration-none">
                  <i class="bi bi-plus-circle me-1"></i>Add New Field
                </a>
              </div>
            </div>

            <!-- Boundary warning banner -->
            <div *ngIf="selectedField && !selectedField.hasBoundary"
                 class="alert alert-warning d-flex align-items-start"
                 role="alert">
              <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div>
                <strong>This field has no boundary defined.</strong> A boundary is required for analysis.
                <a [routerLink]="['/logbook/fields', selectedField.id, 'edit']" class="alert-link ms-1">Add Boundary</a>
              </div>
            </div>

            <!-- Selected field info -->
            <div *ngIf="selectedField" class="alert alert-light border">
              <div class="row g-2">
                <div class="col-md-3">
                  <small class="text-muted d-block">Field Name</small>
                  <span class="fw-medium">{{ selectedField.name }}</span>
                </div>
                <div class="col-md-3">
                  <small class="text-muted d-block">Acreage</small>
                  <span class="fw-medium">{{ selectedField.acreage }} acres</span>
                </div>
                <div class="col-md-3">
                  <small class="text-muted d-block">Crop Type</small>
                  <span class="fw-medium">{{ selectedField.cropType }}</span>
                </div>
                <div *ngIf="selectedField.customerName" class="col-md-3">
                  <small class="text-muted d-block">Customer</small>
                  <span class="fw-medium">{{ selectedField.customerName }}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- ============ STEP 2: Select Map ============ -->
        <div *ngIf="currentStep() === 2">
          <h5 class="mb-3"><i class="bi bi-map me-2"></i>Select Map</h5>
          <p class="text-muted mb-4">Choose a processed map to run analysis on.</p>

          <form [formGroup]="mapForm">
            <!-- Loading state -->
            <div *ngIf="isLoadingMaps()" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Loading maps...
            </div>

            <!-- No maps available -->
            <div *ngIf="!isLoadingMaps() && maps().length === 0" class="alert alert-info">
              <i class="bi bi-info-circle me-2"></i>
              <strong>No ready maps available for this field.</strong>
              Upload and process maps from the
              <a [routerLink]="['/logbook/fields', fieldForm.get('fieldId')?.value, 'edit']" class="alert-link">field edit page</a>
              first.
            </div>

            <!-- Map selection -->
            <div *ngIf="!isLoadingMaps() && maps().length > 0">
              <label for="mapSelect" class="form-label">Map <span class="text-danger">*</span></label>
              <select id="mapSelect" class="form-select" formControlName="mapId">
                <option value="">-- Select a map --</option>
                <option *ngFor="let map of maps()" [value]="map.id">
                  {{ map.mapName }} ({{ map.mapDate | date:'mediumDate' }} - {{ map.cameraType }})
                </option>
              </select>

              <!-- Selected map info -->
              <div *ngIf="selectedMap" class="alert alert-light border mt-3">
                <div class="row g-2">
                  <div class="col-md-4">
                    <small class="text-muted d-block">Map Name</small>
                    <span class="fw-medium">{{ selectedMap.mapName }}</span>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted d-block">Capture Date</small>
                    <span class="fw-medium">{{ selectedMap.mapDate | date:'mediumDate' }}</span>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted d-block">Camera Type</small>
                    <span class="fw-medium">{{ selectedMap.cameraType }}</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- ============ STEP 3: Analysis Options ============ -->
        <div *ngIf="currentStep() === 3">
          <h5 class="mb-3"><i class="bi bi-sliders me-2"></i>Analysis Options</h5>
          <p class="text-muted mb-4">Configure the analysis parameters.</p>

          <form [formGroup]="analysisForm">
            <div class="mb-3">
              <label for="analysisType" class="form-label">Analysis Type <span class="text-danger">*</span></label>
              <select id="analysisType" class="form-select" formControlName="analysisTypeId" (change)="onAnalysisTypeChange()">
                <option value="">-- Select analysis type --</option>
                <option *ngFor="let type of analysisTypes()" [value]="type.id">
                  {{ type.name }}
                </option>
              </select>
              <div *ngIf="selectedAnalysisTypeDescription" class="alert alert-light border mt-2 mb-0 py-2 px-3">
                <small><i class="bi bi-info-circle me-1"></i>{{ selectedAnalysisTypeDescription }}</small>
              </div>
              <div *ngIf="analysisForm.get('analysisTypeId')?.touched && analysisForm.get('analysisTypeId')?.invalid" class="invalid-feedback d-block">
                Please select an analysis type.
              </div>
            </div>

            <div class="mb-3">
              <label for="cropType" class="form-label">Crop Type</label>
              <input id="cropType" type="text" class="form-control" formControlName="cropType" placeholder="e.g., Corn, Soybeans, Wheat">
              <div class="form-text">Helps improve analysis accuracy.</div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="cropVariety" class="form-label">Crop Variety</label>
                <input id="cropVariety" type="text" class="form-control" formControlName="cropVariety" placeholder="e.g., Pioneer P1093, DeKalb DKC62-08">
                <div class="form-text">Specific seed variety or hybrid.</div>
              </div>

              <div class="col-md-6 mb-3">
                <label for="growingStage" class="form-label">Growing Stage</label>
                <select id="growingStage" class="form-select" formControlName="growingStage">
                  <option value="">-- Select growing stage --</option>
                  <optgroup label="Vegetative">
                    <option value="VE">VE - Emergence</option>
                    <option value="V1">V1</option>
                    <option value="V2">V2</option>
                    <option value="V3">V3</option>
                    <option value="V4">V4</option>
                    <option value="V6">V6</option>
                    <option value="V8">V8</option>
                    <option value="V10">V10</option>
                    <option value="V12">V12</option>
                    <option value="VT">VT - Tasseling</option>
                  </optgroup>
                  <optgroup label="Reproductive">
                    <option value="R1">R1 - Silking</option>
                    <option value="R2">R2 - Blister</option>
                    <option value="R3">R3 - Milk</option>
                    <option value="R4">R4 - Dough</option>
                    <option value="R5">R5 - Dent</option>
                    <option value="R6">R6 - Maturity</option>
                  </optgroup>
                  <optgroup label="General">
                    <option value="seedling">Seedling</option>
                    <option value="vegetative">Vegetative</option>
                    <option value="flowering">Flowering</option>
                    <option value="mature">Mature</option>
                  </optgroup>
                </select>
                <div class="form-text">Current growth stage of the crop.</div>
              </div>
            </div>

            <div *ngIf="requiresSeedingDensity" class="mb-3">
              <label for="seedingDensity" class="form-label">Seeding Density (seeds/acre) <span class="text-danger">*</span></label>
              <input id="seedingDensity" type="number" class="form-control" formControlName="seedingDensity"
                     placeholder="e.g., 32000" min="1">
              <div class="form-text">Required for stand count analysis.</div>
              <div *ngIf="analysisForm.get('seedingDensity')?.touched && analysisForm.get('seedingDensity')?.invalid" class="invalid-feedback d-block">
                Please enter a valid seeding density.
              </div>
            </div>
          </form>
        </div>

        <!-- ============ STEP 4: Review Details ============ -->
        <!-- Step 4 is review-only — the actual Pay CTA and Stripe redirect
             live on Step 5. Keeping payment terminology off this step
             prevents the misleading "Review & Pay" header that nudges
             users to expect a charge button here. -->
        <div *ngIf="currentStep() === 4">
          <h5 class="mb-3"><i class="bi bi-clipboard-check me-2"></i>Review Details</h5>
          <p class="text-muted mb-4">Confirm the analysis details below. The next step is payment — once you pay, the analysis kicks off immediately.</p>

          <!-- Checkout error (set by payAndSubmit when Stripe session creation fails) -->
          <div *ngIf="checkoutError()" class="alert alert-danger mb-4">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ checkoutError() }}
          </div>

          <div class="row g-4">
            <!-- Field Information -->
            <div class="col-md-6">
              <div class="card h-100 bg-canvas">
                <div class="card-body">
                  <h6 class="card-title text-muted mb-3"><i class="bi bi-geo-alt me-2"></i>Field</h6>
                  <p class="mb-1 fw-medium">{{ selectedField?.name }}</p>
                  <div class="row g-2 mt-1">
                    <div class="col-6">
                      <small class="text-muted d-block">Acreage</small>
                      <span class="small">{{ selectedField?.acreage }} acres</span>
                    </div>
                    <div class="col-6">
                      <small class="text-muted d-block">Crop Type</small>
                      <span class="small">{{ selectedField?.cropType }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Map -->
            <div class="col-md-6">
              <div class="card h-100 bg-canvas">
                <div class="card-body">
                  <h6 class="card-title text-muted mb-3"><i class="bi bi-map me-2"></i>Map</h6>
                  <p class="mb-1 fw-medium">{{ reviewSummary.mapName }}</p>
                  <p class="mb-0 small text-muted">Date: {{ reviewSummary.mapDate | date:'mediumDate' }} | Camera: {{ reviewSummary.cameraType }}</p>
                </div>
              </div>
            </div>

            <!-- Analysis Details -->
            <div class="col-12">
              <div class="card bg-canvas">
                <div class="card-body">
                  <h6 class="card-title text-muted mb-3"><i class="bi bi-sliders me-2"></i>Analysis Details</h6>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <small class="text-muted d-block">Analysis Type</small>
                      <span class="fw-medium">{{ reviewSummary.analysisType }}</span>
                    </div>
                    <div *ngIf="reviewSummary.cropType" class="col-md-4">
                      <small class="text-muted d-block">Crop Type</small>
                      <span class="fw-medium">{{ reviewSummary.cropType }}</span>
                    </div>
                    <div *ngIf="reviewSummary.cropVariety" class="col-md-4">
                      <small class="text-muted d-block">Crop Variety</small>
                      <span class="fw-medium">{{ reviewSummary.cropVariety }}</span>
                    </div>
                    <div *ngIf="reviewSummary.growingStage" class="col-md-4">
                      <small class="text-muted d-block">Growing Stage</small>
                      <span class="fw-medium">{{ reviewSummary.growingStage }}</span>
                    </div>
                    <div *ngIf="reviewSummary.seedingDensity" class="col-md-4">
                      <small class="text-muted d-block">Seeding Density</small>
                      <span class="fw-medium">{{ reviewSummary.seedingDensity | number }} seeds/acre</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ STEP 5: Payment ============ -->
        <!-- Paywall lands here. Boundary creation + map sync remain free in earlier steps.
             Hosted Stripe Checkout is wired live (billing-payments § 9.4): payAndSubmit()
             mints a session via the Hub and window.location.assign()s to Stripe; the
             return route /map-analysis/purchase/return then finalizes the analysis. -->
        <div *ngIf="currentStep() === 5">
          <h4 class="mb-3"><i class="bi bi-credit-card me-2"></i>Pay &amp; Run Analysis</h4>
          <p class="text-muted mb-4">
            One last step — review your order and pay to kick off the analysis.
          </p>

          <!-- Order summary card -->
          <div class="card border-primary mb-4">
            <div class="card-header bg-primary bg-opacity-10 border-primary">
              <h6 class="mb-0 fw-bold"><i class="bi bi-cart-check me-2"></i>Order Summary</h6>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div class="fw-medium">Map Analysis</div>
                  <small class="text-muted">
                    {{ billedAcresLabel }} <span *ngIf="selectedField">on {{ selectedField.name }}</span> —
                    billed at
                    <span *ngIf="!quoteLoading()">{{ perAcreLabel }}</span>
                    <span *ngIf="quoteLoading()" class="spinner-border spinner-border-sm" role="status" aria-label="Loading price"></span>.
                    <span *ngIf="quote() && quote()!.requestedAcres !== quote()!.billedAcres" class="d-block mt-1">
                      Partial acres are rounded up to the next whole acre ({{ quote()!.requestedAcres }} → {{ quote()!.billedAcres }}).
                    </span>
                  </small>
                </div>
                <div class="text-end fw-bold">
                  <span *ngIf="!quoteLoading()">{{ totalLabel }}</span>
                  <span *ngIf="quoteLoading()" class="spinner-border spinner-border-sm" role="status" aria-label="Loading total"></span>
                </div>
              </div>
              <div class="border-top pt-3 d-flex justify-content-between align-items-center">
                <span class="text-muted">Total due today</span>
                <span class="h4 mb-0 fw-bold">
                  <span *ngIf="!quoteLoading()">{{ totalLabel }}</span>
                  <span *ngIf="quoteLoading()" class="spinner-border" role="status" aria-label="Loading total"></span>
                </span>
              </div>
              <div *ngIf="quoteError()" class="alert alert-warning mt-3 mb-0" role="alert">
                {{ quoteError() }}
              </div>
            </div>
          </div>

          <!-- What happens next explainer -->
          <div class="alert alert-light border d-flex align-items-start mb-4">
            <i class="bi bi-shield-check me-2 mt-1 text-success"></i>
            <small class="mb-0">
              You'll be redirected to a secure Stripe Checkout page to enter your payment details. After payment is confirmed, your analysis will start automatically.
            </small>
          </div>

          <!-- Checkout error display -->
          <div *ngIf="checkoutError()" class="alert alert-danger d-flex align-items-start" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
            <div class="flex-grow-1">{{ checkoutError() }}</div>
          </div>

          <!-- In-flight overlay shown while the Hub mints the Stripe Checkout
               session and the browser is about to navigate to Stripe. -->
          <div *ngIf="isCheckoutInFlight()" class="card bg-light border-0 mt-3">
            <div class="card-body text-center py-4">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="visually-hidden">Working...</span>
              </div>
              <div *ngIf="checkoutStage() === 'creating-session'" class="mb-1 fw-medium">Preparing secure payment...</div>
              <div *ngIf="checkoutStage() === 'redirecting'" class="mb-1 fw-medium">Redirecting to secure payment...</div>
              <small class="text-muted">This usually takes a few seconds.</small>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
          <button *ngIf="currentStep() > 1" class="btn btn-outline-secondary" (click)="previousStep()" [disabled]="isCheckoutInFlight()">
            <i class="bi bi-arrow-left me-2"></i>Previous
          </button>
          <div *ngIf="currentStep() === 1"></div>

          <div class="d-flex gap-2">
            <button *ngIf="currentStep() < totalSteps" class="btn btn-primary" (click)="nextStep()" [disabled]="!canGoNext">
              <span *ngIf="currentStep() === 4">Continue to Payment</span>
              <span *ngIf="currentStep() !== 4">Next</span>
              <i class="bi bi-arrow-right ms-2"></i>
            </button>
            <!-- This button only renders on Step 5 (currentStep() === totalSteps).
                 Disabled until the quote loads so the label doesn't show
                 "Pay — & Run Analysis" and so we never mint a Checkout
                 session for an unknown price. Falls back to the generic
                 label when the quote is still in flight (rare; happens
                 only on first Step-5 paint or after a transient error
                 while the quote refetches). -->
            <button *ngIf="currentStep() === totalSteps" class="btn btn-primary" (click)="payAndSubmit()" [disabled]="isCheckoutInFlight() || !isQuoteReady">
              <span *ngIf="!isCheckoutInFlight() && isQuoteReady"><i class="bi bi-credit-card me-2"></i>Pay {{ totalLabel }} &amp; Run Analysis</span>
              <span *ngIf="!isCheckoutInFlight() && !isQuoteReady"><i class="bi bi-credit-card me-2"></i>Pay &amp; Run Analysis</span>
              <span *ngIf="isCheckoutInFlight()">
                <span class="spinner-border spinner-border-sm me-2"></span>Processing...
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Sidebar Help Panel -->
  <div class="col-lg-4">
    <div class="card">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-question-circle me-2"></i>Help</h5>
      </div>
      <div class="card-body">

        <!-- Step 1: Select Field Help -->
        <div *ngIf="currentStep() === 1">
          <h6 class="mb-2"><i class="bi bi-geo-alt me-2"></i>Field Boundary Required</h6>
          <p class="small mb-3">
            A field boundary is required for Agremo to perform analysis on your drone imagery.
            Boundaries define the geographic area to be analyzed and are used to calculate acreage, clip imagery, and generate accurate results.
          </p>
          <p class="small mb-3">
            If your selected field does not have a boundary defined, you can add one from the field edit page.
          </p>
          <p class="small mb-0" *ngIf="selectedField">
            <a [routerLink]="['/logbook/fields', selectedField.id, 'edit']">
              <i class="bi bi-pencil-square me-1"></i>Edit {{ selectedField.name }}
            </a>
          </p>
          <div class="text-muted small mt-3 pt-3 border-top">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a></div>
        </div>

        <!-- Step 2: Select Map Help -->
        <div *ngIf="currentStep() === 2">
          <h6 class="mb-2"><i class="bi bi-map me-2"></i>About Map Status</h6>
          <p class="small mb-3">
            A map with "Ready" status means it has been processed by Agremo and is available for analysis.
            Only maps that have completed processing can be selected for analysis requests.
          </p>
          <h6 class="mb-2"><i class="bi bi-cloud-arrow-up me-2"></i>Uploading New Maps</h6>
          <p class="small mb-0">
            You can upload new drone imagery maps from the Field Maps card on the
            <a [routerLink]="['/logbook/fields', fieldForm.get('fieldId')?.value, 'edit']">field edit page</a>.
            Once uploaded, maps are automatically sent to Agremo for processing.
          </p>
          <div class="text-muted small mt-3 pt-3 border-top">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a></div>
        </div>

        <!-- Step 3: Analysis Options Help -->
        <div *ngIf="currentStep() === 3">
          <h6 class="mb-2"><i class="bi bi-sliders me-2"></i>Analysis Types</h6>
          <ul class="small mb-3 ps-3">
            <li class="mb-2">
              <strong>Stand Count</strong> &mdash; Counts individual plants and estimates emergence rate. Requires seeding density for accuracy.
            </li>
            <li class="mb-2">
              <strong>Vegetation Stress</strong> &mdash; Identifies areas of crop stress using NDVI (Normalized Difference Vegetation Index) analysis.
            </li>
            <li class="mb-2">
              <strong>Yield Potential</strong> &mdash; Estimates yield variability across the field to identify high and low performing zones.
            </li>
          </ul>
          <h6 class="mb-2"><i class="bi bi-clock me-2"></i>Processing Time</h6>
          <p class="small mb-0">
            Processing typically takes 30 minutes to 24 hours depending on field size and analysis type.
          </p>
          <div class="text-muted small mt-3 pt-3 border-top">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a></div>
        </div>

        <!-- Step 4: Review Help -->
        <div *ngIf="currentStep() === 4">
          <h6 class="mb-2"><i class="bi bi-info-circle me-2"></i>Review Your Order</h6>
          <p class="small mb-3">
            Double-check the field, map, and analysis options. The next step is payment — once you pay, the analysis kicks off immediately.
          </p>
          <p class="small mb-0">
            Need to change something? Use the stepper above to jump back to any step.
          </p>
          <div class="text-muted small mt-3 pt-3 border-top">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a></div>
        </div>

        <!-- Step 5: Payment Help -->
        <div *ngIf="currentStep() === 5">
          <h6 class="mb-2"><i class="bi bi-credit-card me-2"></i>Payment &amp; Processing</h6>
          <p class="small mb-3">
            Your payment method is charged when you confirm the order. We never see your payment details — Stripe handles it on a secure hosted page.
          </p>
          <p class="small mb-3">
            After payment, your analysis request enters the processing queue. Most analyses complete within 5–10 minutes; some can take up to 24 hours depending on field size.
          </p>
          <p class="small mb-0">
            Results appear on the analysis detail page automatically via real-time updates — no need to refresh.
          </p>
          <div class="text-muted small mt-3 pt-3 border-top">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a> · Payments by Stripe</div>
        </div>

      </div>
    </div>
  </div>
</div>
